import {
  Header,
  SelfServeWidgetsContainer,
  WidgetCard,
  WidgetCards,
  WidgetDescription,
  WidgetDetails,
  WidgetLink,
  WidgetName,
} from './SelfServeWidgets.styled'
import { WidgetCategory } from './_common/WidgetCategory'
import { WidgetCategoryEnum } from './_common/types'
import { Ribbon } from './_common/Ribbon'

type Widget = {
  name: string
  description: string
  path: string
  category?: WidgetCategoryEnum
}

/*
 Self-serve widgets are intended to be fully standalone (no props). Therefore, they are not imported
 here as child components but handled at the routing layer. This allows us to work on features that will
 eventually become customer-facing, lifting them "up and out" into the main application at time of release
 while still being able to interact with (and iterate on) live versions of them here in a self-serve context.
 */
const widgets: Widget[] = [
  {
    name: 'Funder Collaboration',
    description:
      'Create goals for multiple nonprofit organizations and funder cohorts, all at one time',
    path: '/internal/self-serve/funder-shared-goals',
    category: WidgetCategoryEnum.danger,
  },
  {
    name: 'Impact Report',
    description:
      'Preview what ED users will see in their impact report (scheduled email)',
    path: '/internal/self-serve/impact-report',
    category: WidgetCategoryEnum.readOnly,
  },
  {
    name: 'Impact Report - Dashboard',
    description:
      'Dashboard version of the impact report with various widgets and filters',
    path: '/internal/self-serve/impact-report-dashboard',
    category: WidgetCategoryEnum.readOnly,
  },
  {
    name: 'Funder Report',
    description:
      'Dashboard to view Funder Report, including data used to create it and rough output',
    path: '/internal/self-serve/funder-report',
    category: WidgetCategoryEnum.readOnly,
  },
  {
    name: 'Needs Assessment - Trigger New Surveys',
    description: 'Tool to trigger new surveys for specific users',
    path: '/internal/self-serve/needs-assessment/trigger-new-surveys',
    category: WidgetCategoryEnum.danger,
  },
  {
    name: 'Check-In Survey',
    description: 'Tool to trigger new check-in surveys for specific users',
    path: '/internal/self-serve/check-in-survey',
    category: WidgetCategoryEnum.userSpecific,
  },
  {
    name: 'Goals Wizard',
    description: 'Tool to trigger restart of the Goals Wizard flow',
    path: '/internal/self-serve/goals-wizard',
    category: WidgetCategoryEnum.danger,
  },
  {
    name: 'Group Coaching Event',
    description: 'Create a group coaching event',
    path: '/internal/self-serve/coaching-event-management',
    category: WidgetCategoryEnum.danger,
  },
  {
    name: 'Funder Administration',
    description: 'View and manage all funders in the system',
    path: '/internal/self-serve/funder-admin',
    category: WidgetCategoryEnum.readOnly,
  },
  {
    name: 'Cohort Administration',
    description: 'View and manage all cohorts in the system',
    path: '/internal/self-serve/cohort-admin',
    category: WidgetCategoryEnum.readOnly,
  },
  {
    name: 'Nonprofit Administration',
    description: 'View and manage all nonprofits in the system',
    path: '/internal/self-serve/nonprofit-admin',
    category: WidgetCategoryEnum.readOnly,
  },
  {
    name: 'User Administration',
    description: 'View and manage all users in the system',
    path: '/internal/self-serve/user-admin',
    category: WidgetCategoryEnum.readOnly,
  },
  {
    name: 'One on One Coaching',
    description: 'Manage coaching events',
    path: '/internal/self-serve/one-on-one-coaching',
    category: WidgetCategoryEnum.danger,
  },
  // Hidden for now to make it less confusing for coaches who want to manage and send interventions instead of generating them
  // {
  //   name: 'AI Coaching Interventions',
  //   description: 'View and test AI coaching interventions for users',
  //   path: '/internal/self-serve/ai-coaching-admin',
  //   category: WidgetCategoryEnum.danger,
  // },
  {
    name: 'Coach Intervention Review',
    description: 'Review and send AI-generated coaching messages to users',
    path: '/internal/self-serve/coaching-interventions',
    category: WidgetCategoryEnum.danger,
  },
  {
    name: 'Job Dashboard',
    description:
      'Monitor and manage background jobs with ability to retry failed jobs',
    path: '/internal/self-serve/job-admin',
    category: WidgetCategoryEnum.readOnly,
  },
  {
    name: 'Data Visualization Tool',
    description:
      'Generate insights about nonprofits and funders through natural language queries',
    path: '/internal/self-serve/data-visualization',
    category: WidgetCategoryEnum.readOnly,
  },
].sort((a, b) => a.name.localeCompare(b.name))

export const SelfServeWidgets = () => {
  const widgetCards = widgets.map((widget) => (
    <WidgetLink
      to={widget.path}
      style={{ textDecoration: 'none' }}
      key={widget.path}
    >
      <WidgetCard key={widget.path}>
        <WidgetDetails>
          <WidgetName>{widget.name}</WidgetName>
          <WidgetDescription>{widget.description}</WidgetDescription>
        </WidgetDetails>
        {widget.category && <WidgetCategory category={widget.category} />}
      </WidgetCard>
    </WidgetLink>
  ))

  return (
    <SelfServeWidgetsContainer>
      <Ribbon />
      <Header>Self-Serve Widgets</Header>
      <WidgetCards>{widgetCards}</WidgetCards>
    </SelfServeWidgetsContainer>
  )
}
